import { useEffect, useRef, useState, useCallback } from "react";
import type { OpsApi } from "../useOps";
import { SEVERITY_COLOR } from "@/types/api";

/**
 * MapView — mounts the canvas and forwards pointer interaction to the EWMap
 * engine (hover tooltip + click picking for signals/events). The engine itself
 * owns the render loop; React only handles mount + pointer wiring.
 */
export function MapView({ ops }: { ops: OpsApi }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ops.attachCanvas(canvasRef.current);
  }, [ops]);

  function onMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const map = ops.getMap();
    const tip = tipRef.current;
    const canvas = canvasRef.current;
    if (!map || !tip || !canvas) return;
    if (map.dragMoved) {
      tip.style.display = "none";
      return;
    }
    const r = canvas.getBoundingClientRect();
    const scale = r.width / map.W;
    const mx = (e.clientX - r.left) / scale;
    const my = (e.clientY - r.top) / scale;
    const hit = map.pick(mx, my, 14);
    const ehit = hit ? null : map.pickEvent(mx, my, 12);
    if (hit || ehit) {
      tip.style.display = "block";
      tip.style.left = `${Math.min(map.W - 250, mx + 14)}px`;
      tip.style.top = `${Math.min(map.H - 50, my + 12)}px`;
      const sev = hit ? hit.severity : "medium";
      const label = hit ? hit.severity : (ehit?.type ?? "event");
      const title = hit ? hit.title : (ehit?.title ?? "");
      tip.innerHTML = `<span class="tsev" style="color:${
        hit ? SEVERITY_COLOR[sev] : "#f4b942"
      }">${label}</span>${escapeHtml(title)}`;
      canvas.style.cursor = "pointer";
    } else {
      tip.style.display = "none";
      canvas.style.cursor = "crosshair";
    }
  }

  function onClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const map = ops.getMap();
    const canvas = canvasRef.current;
    if (!map || !canvas || map.dragMoved) return;
    const r = canvas.getBoundingClientRect();
    const scale = r.width / map.W;
    const mx = (e.clientX - r.left) / scale;
    const my = (e.clientY - r.top) / scale;
    const hit = map.pick(mx, my);
    if (hit) {
      ops.selectSignal(hit.id);
      return;
    }
    const ehit = map.pickEvent(mx, my);
    if (ehit) ops.selectEvent(ehit.id);
  }

  const [isFs, setIsFs] = useState(false);
  // WHOLE-APP fullscreen (the entire ops console), not just the map element.
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFs(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFs(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  return (
    <main id="map">
      <canvas id="mapCanvas" ref={canvasRef} onMouseMove={onMove} onClick={onClick} />
      <div className="map-ov map-tl">
        <div className="t1" id="mapTitle" title="Global real-time operations plot">
          GLOBAL PLOT
        </div>
        <div className="t2" id="mapSub">
          Mercator · tracking live
        </div>
      </div>
      <div className="map-ov layers" id="layerChips">
        <LayerChips ops={ops} />
      </div>
      <div className="map-ov legend" title="Signal severity classification">
        <div className="it" title="Critical severity — immediate market impact likely">
          <span className="d" style={{ background: "var(--sev-crit)" }} />
          Critical
        </div>
        <div className="it" title="High severity — significant potential impact">
          <span className="d" style={{ background: "var(--sev-high)" }} />
          High
        </div>
        <div className="it" title="Medium severity — notable event, moderate impact">
          <span className="d" style={{ background: "var(--sev-med)" }} />
          Medium
        </div>
        <div className="it" title="Low severity — background activity, informational">
          <span className="d" style={{ background: "var(--sev-low)" }} />
          Low
        </div>
        <div className="it" style={{ opacity: 0.65 }}>
          © OSM · CARTO
        </div>
      </div>
      <button className="map-ov map-reset mbtn" onClick={ops.clearSelection} title="Reset map to world overview and clear selection">
        RESET · WORLD VIEW
      </button>
      <button className="map-ov map-fs zbtn" onClick={toggleFullscreen} title={isFs ? "Exit fullscreen" : "Fullscreen map"}>
        {isFs ? "⊡" : "⛶"}
      </button>
      <div className="map-ov map-zoom">
        <button className="zbtn" onClick={ops.zoomIn} title="Zoom in (or use scroll wheel)">
          +
        </button>
        <button className="zbtn" onClick={ops.zoomOut} title="Zoom out (or use scroll wheel)">
          −
        </button>
      </div>
      <div className="map-ov map-tip" ref={tipRef} />
    </main>
  );
}

const LAYERS: Array<[string, string, string, string]> = [
  ["basemap", "Basemap", "rgba(170,185,178,0.85)", "Toggle satellite basemap tiles"],
  ["maritime", "Maritime", "rgb(95,211,224)", "Show live AIS vessel positions"],
  ["aircraft", "Aircraft", "#d6a36e", "Show live ADS-B aircraft positions"],
  ["satellite", "Satellite", "rgba(95,211,224,0.6)", "Show satellite tracks (increases data load)"],
];

function LayerChips({ ops }: { ops: OpsApi }) {
  return (
    <>
      {LAYERS.map(([k, label, color, tip]) => (
        <LayerChip key={k} k={k} label={label} color={color} tip={tip} ops={ops} />
      ))}
    </>
  );
}

function LayerChip({
  k,
  label,
  color,
  tip,
  ops,
}: {
  k: string;
  label: string;
  color: string;
  tip: string;
  ops: OpsApi;
}) {
  const defaultOn = k === "basemap";
  const onToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const on = el.classList.toggle("off"); // returns true if NOW has 'off'
    ops.toggleLayer(k, !on);
  };
  return (
    <div className={`lchip${defaultOn ? "" : " off"}`} onClick={onToggle} title={tip}>
      <span className="gl" style={{ background: color }} />
      {label}
    </div>
  );
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}
